import { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import SettingsBrightnessRoundedIcon from '@mui/icons-material/SettingsBrightnessRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import { useThemeStore } from '@/store/useThemeStore'
import type { ThemeMode } from '@/types/theme'

const themeOptions: Array<{
  value: ThemeMode
  label: string
  icon: React.ReactNode
}> = [
  {
    value: 'light',
    label: 'Light',
    icon: <LightModeRoundedIcon fontSize="small" />,
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: <DarkModeRoundedIcon fontSize="small" />,
  },
  {
    value: 'system',
    label: 'System',
    icon: <SettingsBrightnessRoundedIcon fontSize="small" />,
  },
]

export const ThemeToggle = () => {
  const { mode, effectiveMode, setMode } = useThemeStore()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectTheme = (selectedMode: ThemeMode) => {
    setMode(selectedMode)
    handleClose()
  }

  const getCurrentIcon = () => {
    if (effectiveMode === 'dark') {
      return <DarkModeRoundedIcon />
    }
    return <LightModeRoundedIcon />
  }

  return (
    <>
      <Tooltip title="Change theme">
        <IconButton color="inherit" onClick={handleOpen} aria-label="Change theme">
          {getCurrentIcon()}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {themeOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSelectTheme(option.value)}
            selected={mode === option.value}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
            {mode === option.value && (
              <CheckRoundedIcon fontSize="small" sx={{ ml: 2 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
